// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import blakejs from 'blakejs';
const { blake2bInit, blake2bUpdate, blake2bFinal } = blakejs;
import { Account } from './account.js';
import { UNITS } from './constants.js';
import { bytes, hex } from './convert.js';
import Ed25519 from './ed25519.js';
import { Rpc } from './rpc.js';
import { SendBlock } from './block.js';
/**
* Converts a decimal amount from one unit divider to another.
*
* @param {bigint|string} amount - Decimal amount to convert
* @param {string} inputUnit - Current denomination
* @param {string} outputUnit - Desired denomination
*/
export async function convert(amount, inputUnit, outputUnit) {
    if (typeof amount === 'bigint') {
        amount = amount.toString();
    }
    if (typeof amount !== 'string' || amount === '' || !/^[0-9]*\.?[0-9]*$/.test(amount)) {
        throw new Error('Invalid amount');
    }
    let [i = '', f = ''] = amount.toString().split('.');
    i = i.replace(/^0*/g, '');
    f = f.replace(/0*$/g, '');
    inputUnit = inputUnit.toUpperCase();
    outputUnit = outputUnit.toUpperCase();
    if (!(inputUnit in UNITS)) {
        throw new Error(`Unknown denomination ${inputUnit}, expected one of the following: ${Object.keys(UNITS)}`);
    }
    if (!(outputUnit in UNITS)) {
        throw new Error(`Unknown denomination ${outputUnit}, expected one of the following: ${Object.keys(UNITS)}`);
    }
    // convert to raw
    i = `${i}${f.slice(0, UNITS[inputUnit])}`.padEnd(i.length + UNITS[inputUnit], '0');
    f = f.slice(UNITS[inputUnit]);
    if (i.length > 39) {
        throw new Error('Amount exceeds Nano limits');
    }
    if (i.length < 1) {
        throw new Error('Amount must be at least 1 raw');
    }
    if (f.length > 0) {
        throw new Error('Amount contains fractional raw');
    }
    // convert to desired denomination
    if (UNITS[outputUnit] !== 0) {
        f = i.padStart(40, '0').slice(-UNITS[outputUnit]) + f;
        i = i.slice(0, -UNITS[outputUnit]);
    }
    i = i.replace(/^0*/g, '') ?? '0';
    f = f.replace(/0*$/g, '');
    return `${i}${f ? '.' : ''}${f}`;
}
/**
* Hashes data using BLAKE2b.
*
* @param {string|string[]} data - Hexadecimal-formatted data to hash
* @returns {Promise<Uint8Array>} Array of bytes representing hashed input data
*/
export async function blake2b(data) {
    if (!Array.isArray(data))
        data = [data];
    const ctx = blake2bInit(32, undefined);
    data.forEach(str => blake2bUpdate(ctx, hex.toBytes(str)));
    return blake2bFinal(ctx);
}
/**
* Converts one or more strings to hexadecimal and hashes them with BLAKE2b.
*
* @param {string|string[]} data - Input to hash
* @returns {Promise<string>} Hash of the input
*/
export async function hash(data) {
    if (!Array.isArray(data))
        data = [data];
    const enc = new TextEncoder();
    data = data.map(str => { return bytes.toHex(enc.encode(str)); });
    const hash = await blake2b(data);
    return bytes.toHex(hash);
}
/**
* Signs arbitrary strings with a private key using the Ed25519 signature scheme.
*
* @param {string} key - Hexadecimal-formatted private key to use for signing
* @param {...string} input - Data to be signed
* @returns {Promise<string>} Hexadecimal-formatted signature
*/
export async function sign(key, ...input) {
    const enc = new TextEncoder();
    const data = input.map(str => { return bytes.toHex(enc.encode(str)); });
    const signature = Ed25519.sign(await blake2b(data), hex.toBytes(key));
    return bytes.toHex(signature);
}
/**
* Collects the funds from a specified range of accounts in a wallet and sends
* them all to a single recipient address.
*
* @param {Rpc|string|URL} rpc - RPC node information required to refresh accounts, calculate PoW, and process blocks
* @param {Blake2bWallet|Bip44Wallet|LedgerWallet} wallet - Wallet from which to sweep funds
* @param {string} recipient - Destination address for all swept funds
* @param {number} from - Starting account index to sweep
* @param {number} to - Ending account index to sweep
* @returns An array of results including both successes and failures
 */
export async function sweep(rpc, wallet, recipient, from = 0, to = from) {
    if (rpc == null || wallet == null || recipient == null) {
        throw new ReferenceError('Missing required sweep arguments');
    }
    if (typeof rpc === 'string' || rpc.constructor === URL) {
        rpc = new Rpc(rpc);
    }
    if (rpc.constructor !== Rpc) {
        throw new TypeError('RPC must be a valid node');
    }
    const blockQueue = [];
    const results = [];
    const recipientAccount = new Account(recipient);
    const accounts = await wallet.refresh(rpc, from, to);
    for (const account of accounts) {
        if (account.representative?.address && account.frontier) {
            const block = new SendBlock(account, account.balance?.toString() ?? '0', recipientAccount.address, account.balance?.toString() ?? '0', account.representative.address, account.frontier);
            blockQueue.push(new Promise(async (resolve) => {
                try {
                    await block.pow(rpc);
                    await block.sign(account.index);
                    const hash = await block.process(rpc);
                    results.push({ status: 'success', address: block.account.address, message: hash });
                }
                catch (err) {
                    results.push({ status: 'error', address: block.account.address, message: err.message });
                }
                finally {
                    resolve(null);
                }
            }));
        }
    }
    await Promise.allSettled(blockQueue);
    return results;
}
/**
* Verifies the signature of arbitrary strings using a public key.
*
* @param {string} key - Hexadecimal-formatted public key to use for verification
* @param {string} signature - Hexadcimal-formatted signature
* @param {...string} input - Data to be verified
* @returns {boolean} True if the data was signed by the public key's matching private key
*/
export async function verify(key, signature, ...input) {
    const enc = new TextEncoder();
    const data = input.map(str => { return bytes.toHex(enc.encode(str)); });
    return Ed25519.verify(await blake2b(data), hex.toBytes(key), hex.toBytes(signature));
}
export default { blake2b, convert, hash, sign, sweep, verify };
